// src/pages/SuperAdminPage.js

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getFunctions, httpsCallable } from "firebase/functions";
import { app, db } from '../firebase/config';
import { collection, getDocs, query, where } from 'firebase/firestore';
import TenantRequestsManager from '../components/admin/TenantRequestsManager';
import ManualTenantWizard from '../components/ManualTenantWizard';

// A helper component for styled input fields
const FormInput = ({ label, id, ...props }) => (
  <div style={{ marginBottom: '15px' }}>
    <label htmlFor={id} style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
      {label}
    </label>
    <input
      id={id}
      {...props}
      style={{
        width: '100%',
        padding: '10px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        boxSizing: 'border-box',
      }}
    />
  </div>
);

// Simple Invitation Section Component
const SimpleInvitationSection = ({ onInviteSent }) => {
  const [inviteData, setInviteData] = useState({
    schoolName: '',
    adminName: '',
    adminEmail: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const handleInputChange = (field, value) => {
    setInviteData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!inviteData.schoolName || !inviteData.adminName || !inviteData.adminEmail) {
      setErrorMessage('All fields are required.');
      return;
    }

    setIsSubmitting(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const response = await fetch('https://us-east1-relief-roster-43593.cloudfunctions.net/sendSchoolInvitation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          schoolName: inviteData.schoolName,
          adminName: inviteData.adminName,
          adminEmail: inviteData.adminEmail,
          invitedBy: 'Kyle Sumrow',
          invitedAt: new Date().toISOString()
        })
      });

      const result = await response.json();

      if (result.success) {
        setSuccessMessage(`✅ Invitation sent to ${inviteData.adminEmail} for ${inviteData.schoolName}!`);
        setInviteData({
          schoolName: '',
          adminName: '',
          adminEmail: ''
        });
        if (onInviteSent) onInviteSent(result);
      } else {
        setErrorMessage(`❌ Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      setErrorMessage(`❌ Error sending invitation: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxSizing: 'border-box',
    fontSize: '14px',
    fontFamily: 'sans-serif'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '5px',
    fontWeight: 'bold',
    color: '#333',
    fontSize: '14px'
  };

  return (
    <div style={{ 
      padding: '20px', 
      border: '1px solid #28a745', 
      borderRadius: '8px', 
      backgroundColor: '#f8fff9',
      marginBottom: '20px'
    }}>
      <h3 style={{ color: '#28a745', marginTop: 0, marginBottom: '15px' }}>
        📧 Invite New School
      </h3>
      <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
        Send an invitation email to a school admin with a link to complete their onboarding.
      </p>
      
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
          <div>
            <label style={labelStyle}>
              School Name <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              value={inviteData.schoolName}
              onChange={(e) => handleInputChange('schoolName', e.target.value)}
              placeholder="e.g., Northwood High School"
              style={inputStyle}
              required
            />
          </div>
          
          <div>
            <label style={labelStyle}>
              Admin Name <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              value={inviteData.adminName}
              onChange={(e) => handleInputChange('adminName', e.target.value)}
              placeholder="e.g., John Smith"
              style={inputStyle}
              required
            />
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>
            Admin Email Address <span style={{ color: 'red' }}>*</span>
          </label>
          <input
            type="email"
            value={inviteData.adminEmail}
            onChange={(e) => handleInputChange('adminEmail', e.target.value)}
            placeholder="admin@school.org"
            style={inputStyle}
            required
          />
        </div>

        {successMessage && (
          <div style={{ 
            padding: '10px', 
            backgroundColor: '#d4edda', 
            border: '1px solid #c3e6cb',
            borderRadius: '4px',
            color: '#155724',
            marginBottom: '15px',
            fontSize: '14px'
          }}>
            {successMessage}
          </div>
        )}

        {errorMessage && (
          <div style={{ 
            padding: '10px', 
            backgroundColor: '#f8d7da', 
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            color: '#721c24',
            marginBottom: '15px',
            fontSize: '14px'
          }}>
            {errorMessage}
          </div>
        )}

        <button 
          type="submit" 
          disabled={isSubmitting}
          style={{ 
            width: '100%', 
            padding: '12px 20px', 
            fontSize: '14px',
            fontWeight: 'bold',
            backgroundColor: isSubmitting ? '#6c757d' : '#28a745', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px', 
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s'
          }}
        >
          {isSubmitting ? '📤 Sending Invitation...' : '📧 Send School Invitation'}
        </button>
      </form>

      <div style={{ marginTop: '15px', fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
        <strong>What happens:</strong> The school admin will receive an email with a secure link to complete their Relief Roster setup. 
        After they submit their information, you'll see their request in "Pending Approvals" for review.
      </div>
    </div>
  );
};

// Edit User Modal Component
const EditUserModal = ({ user, onClose, onSuccess }) => {
  const [userData, setUserData] = useState({
    email: user.email || '',
    name: user.name || '',
    displayName: user.displayName || '',
    role: user.role || '',
    status: user.status || 'active',
    title: user.Title || user.title || '',
    sis_id: user.sis_id || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleInputChange = (field, value) => {
    setUserData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const functions = getFunctions(app, 'us-east1');
      const updateTenantUser = httpsCallable(functions, 'updateTenantUser');
      
      const result = await updateTenantUser({
        userId: user.id,
        tenantId: user.tenantId,
        updates: userData
      });

      if (result.data.success) {
        setSuccess('User updated successfully!');
        setTimeout(() => {
          onSuccess();
        }, 1500);
      } else {
        throw new Error(result.data.error || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      setError(error.message || 'An error occurred while updating the user');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 2000
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '8px',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <h2 style={{ marginTop: 0 }}>Edit User</h2>
        <p style={{ color: '#666', marginBottom: '20px', fontSize: '14px' }}>
          User ID: <code>{user.id}</code>
        </p>

        {error && (
          <div style={{ 
            padding: '10px', 
            backgroundColor: '#f8d7da', 
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            color: '#721c24',
            marginBottom: '15px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{ 
            padding: '10px', 
            backgroundColor: '#d4edda', 
            border: '1px solid #c3e6cb',
            borderRadius: '4px',
            color: '#155724',
            marginBottom: '15px',
            fontSize: '14px'
          }}>
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <FormInput
            label="Name"
            id="name"
            type="text"
            value={userData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
          />

          <FormInput
            label="Display Name"
            id="displayName"
            type="text"
            value={userData.displayName}
            onChange={(e) => handleInputChange('displayName', e.target.value)}
          />

          <FormInput
            label="Email"
            id="email"
            type="email"
            value={userData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
          />

          <FormInput
            label="Title"
            id="title"
            type="text"
            value={userData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
          />

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
              Role
            </label>
            <select
              value={userData.role}
              onChange={(e) => handleInputChange('role', e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxSizing: 'border-box'
              }}
            >
              <option value="">Select Role</option>
              <option value="System Administrator">System Administrator</option>
              <option value="Educational Administrator">Educational Administrator</option>
              <option value="HR">HR</option>
              <option value="Faculty/Staff">Faculty/Staff</option>
              <option value="Staff">Staff</option>
              <option value="Outside Sub">Outside Sub</option>
              <option value="Volunteer Manager">Volunteer Manager</option>
              <option value="Volunteer">Volunteer</option>
            </select>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
              Status
            </label>
            <select
              value={userData.status}
              onChange={(e) => handleInputChange('status', e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxSizing: 'border-box'
              }}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <FormInput
            label="SIS ID"
            id="sis_id"
            type="text"
            value={userData.sis_id}
            onChange={(e) => handleInputChange('sis_id', e.target.value)}
          />

          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: isSubmitting ? '#6c757d' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontWeight: 'bold'
              }}
            >
              {isSubmitting ? 'Updating...' : 'Update User'}
            </button>
            
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Tenant Details Modal Component - Enhanced with User Management
const TenantDetailsModal = ({ tenant, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('stats');
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUserId, setDeletingUserId] = useState(null);

  useEffect(() => {
    fetchTenantStats();
    if (activeTab === 'users') {
      fetchTenantUsers();
    }
  }, [tenant.tenantId, activeTab]);

  const fetchTenantStats = async () => {
    try {
      setLoading(true);
      const functions = getFunctions(app, 'us-east1');
      const getTenantStats = httpsCallable(functions, 'getTenantStats');
      
      const result = await getTenantStats({ tenantId: tenant.tenantId });
      
      if (result.data.success) {
        setStats(result.data.stats);
      } else {
        throw new Error(result.data.error || 'Failed to fetch tenant statistics');
      }
    } catch (error) {
      console.error('Error fetching tenant stats:', error);
      setError(error.message || 'An error occurred while fetching statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchTenantUsers = async () => {
    try {
      setLoading(true);
      const functions = getFunctions(app, 'us-east1');
      const getTenantUsers = httpsCallable(functions, 'getTenantUsers');
      
      const result = await getTenantUsers({ tenantId: tenant.tenantId });
      
      if (result.data.success) {
        setUsers(result.data.users);
      } else {
        throw new Error(result.data.error || 'Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError(error.message || 'An error occurred while fetching users');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (user) => {
    const newStatus = user.status === 'active' ? 'inactive' : 'active';
    
    try {
      const functions = getFunctions(app, 'us-east1');
      const updateTenantUser = httpsCallable(functions, 'updateTenantUser');
      
      const result = await updateTenantUser({
        userId: user.id,
        tenantId: tenant.tenantId,
        updates: { status: newStatus }
      });

      if (result.data.success) {
        // Update local state
        setUsers(users.map(u => u.id === user.id ? { ...u, status: newStatus } : u));
        alert(`✅ User ${newStatus === 'active' ? 'activated' : 'inactivated'} successfully`);
      } else {
        throw new Error(result.data.error || 'Failed to update user status');
      }
    } catch (error) {
      console.error('Error updating user status:', error);
      alert(`❌ Error: ${error.message}`);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Are you sure you want to delete ${user.name || user.displayName || user.email}? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingUserId(user.id);
      const functions = getFunctions(app, 'us-east1');
      const deleteTenantUser = httpsCallable(functions, 'deleteTenantUser');
      
      const result = await deleteTenantUser({
        userId: user.id,
        tenantId: tenant.tenantId
      });

      if (result.data.success) {
        setUsers(users.filter(u => u.id !== user.id));
        alert('✅ User deleted successfully');
      } else {
        throw new Error(result.data.error || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(`❌ Error: ${error.message}`);
    } finally {
      setDeletingUserId(null);
    }
  };

  const StatCard = ({ title, value, icon, color = '#007bff' }) => (
    <div style={{
      padding: '20px',
      backgroundColor: 'white',
      border: `2px solid ${color}`,
      borderRadius: '8px',
      textAlign: 'center'
    }}>
      <div style={{ fontSize: '32px', marginBottom: '10px' }}>{icon}</div>
      <div style={{ fontSize: '28px', fontWeight: 'bold', color: color, marginBottom: '5px' }}>
        {value}
      </div>
      <div style={{ fontSize: '14px', color: '#666' }}>{title}</div>
    </div>
  );

  const RoleBreakdownCard = ({ role, count, color }) => (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 15px',
      backgroundColor: '#f8f9fa',
      borderRadius: '6px',
      marginBottom: '8px',
      border: '1px solid #e9ecef'
    }}>
      <span style={{ fontWeight: '500', color: '#333' }}>{role}</span>
      <span style={{ 
        fontSize: '18px', 
        fontWeight: 'bold', 
        color: color,
        backgroundColor: 'white',
        padding: '4px 12px',
        borderRadius: '4px'
      }}>
        {count}
      </span>
    </div>
  );

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '12px',
        maxWidth: '1100px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
          <div>
            <h2 style={{ margin: 0, marginBottom: '5px' }}>
              📊 {tenant.name || tenant.schoolName}
            </h2>
            <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
              Tenant ID: <code>{tenant.tenantId}</code>
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div style={{ 
          display: 'flex', 
          gap: '10px', 
          marginBottom: '25px',
          borderBottom: '2px solid #dee2e6'
        }}>
          <button
            onClick={() => setActiveTab('stats')}
            style={{
              padding: '10px 20px',
              border: 'none',
              backgroundColor: activeTab === 'stats' ? '#007bff' : 'transparent',
              color: activeTab === 'stats' ? 'white' : '#007bff',
              borderRadius: '4px 4px 0 0',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '14px'
            }}
          >
            📈 Statistics
          </button>
          <button
            onClick={() => {
              setActiveTab('users');
              if (users.length === 0) fetchTenantUsers();
            }}
            style={{
              padding: '10px 20px',
              border: 'none',
              backgroundColor: activeTab === 'users' ? '#007bff' : 'transparent',
              color: activeTab === 'users' ? 'white' : '#007bff',
              borderRadius: '4px 4px 0 0',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '14px'
            }}
          >
            👥 Manage Users
          </button>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>⏳</div>
            <p>Loading...</p>
          </div>
        )}

        {error && (
          <div style={{ 
            padding: '15px', 
            backgroundColor: '#f8d7da', 
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            color: '#721c24',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        {/* Statistics Tab */}
        {!loading && !error && activeTab === 'stats' && stats && (
          <>
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ marginBottom: '15px', color: '#333' }}>📈 Overview</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                <StatCard 
                  title="Total Users" 
                  value={stats.totalUsers || 0} 
                  icon="👥" 
                  color="#007bff"
                />
                <StatCard 
                  title="Active Users" 
                  value={stats.activeUsers || 0} 
                  icon="✅" 
                  color="#28a745"
                />
                <StatCard 
                  title="Recent Activity (7d)" 
                  value={stats.recentActivityCount || 0} 
                  icon="🔥" 
                  color="#fd7e14"
                />
                <StatCard 
                  title="Last Activity" 
                  value={stats.lastActivityDays ? `${stats.lastActivityDays}d ago` : 'N/A'} 
                  icon="📅" 
                  color="#6f42c1"
                />
              </div>
            </div>

            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ marginBottom: '15px', color: '#333' }}>👥 Users by Role</h3>
              <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px' }}>
                {stats.usersByRole && Object.keys(stats.usersByRole).length > 0 ? (
                  Object.entries(stats.usersByRole)
                    .sort((a, b) => b[1] - a[1])
                    .map(([role, count]) => (
                      <RoleBreakdownCard 
                        key={role}
                        role={role}
                        count={count}
                        color="#007bff"
                      />
                    ))
                ) : (
                  <p style={{ textAlign: 'center', color: '#666', margin: 0 }}>No users found</p>
                )}
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ marginBottom: '15px', color: '#333' }}>📊 Activity Metrics</h3>
              <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div style={{ padding: '15px', backgroundColor: 'white', borderRadius: '6px' }}>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>LOGINS (Last 7 Days)</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#007bff' }}>
                      {stats.loginsLast7Days || 0}
                    </div>
                  </div>
                  <div style={{ padding: '15px', backgroundColor: 'white', borderRadius: '6px' }}>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>LOGINS (Last 30 Days)</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>
                      {stats.loginsLast30Days || 0}
                    </div>
                  </div>
                  <div style={{ padding: '15px', backgroundColor: 'white', borderRadius: '6px' }}>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>RECORDS CREATED (Last 7 Days)</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#fd7e14' }}>
                      {stats.recordsCreatedLast7Days || 0}
                    </div>
                  </div>
                  <div style={{ padding: '15px', backgroundColor: 'white', borderRadius: '6px' }}>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>RECORDS CREATED (Last 30 Days)</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#6f42c1' }}>
                      {stats.recordsCreatedLast30Days || 0}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                  {stats.engagementLevel && (
                    <span style={{
                      padding: '10px 20px',
                      borderRadius: '20px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      backgroundColor: 
                        stats.engagementLevel === 'high' ? '#d4edda' :
                        stats.engagementLevel === 'medium' ? '#fff3cd' : '#f8d7da',
                      color:
                        stats.engagementLevel === 'high' ? '#155724' :
                        stats.engagementLevel === 'medium' ? '#856404' : '#721c24'
                    }}>
                      {stats.engagementLevel === 'high' ? '🔥 High Engagement' :
                       stats.engagementLevel === 'medium' ? '👍 Medium Engagement' : '⚠️ Low Engagement'}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div style={{ 
              padding: '15px', 
              backgroundColor: '#e7f3ff', 
              borderRadius: '8px',
              fontSize: '13px',
              color: '#004085'
            }}>
              <strong>💡 Tip:</strong> Low engagement? Consider reaching out to check if they need training or support!
            </div>
          </>
        )}

        {/* Users Tab */}
        {!loading && !error && activeTab === 'users' && (
          <div>
            <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Users ({users.length})</h3>
              <button
                onClick={fetchTenantUsers}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                🔄 Refresh
              </button>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Name</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Email</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Role</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Status</th>
                    <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                        No users found
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => (
                      <tr key={user.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                        <td style={{ padding: '10px' }}>{user.name || user.displayName || 'N/A'}</td>
                        <td style={{ padding: '10px' }}>{user.email}</td>
                        <td style={{ padding: '10px' }}>{user.role || 'N/A'}</td>
                        <td style={{ padding: '10px' }}>
                          <span style={{ 
                            padding: '2px 8px', 
                            borderRadius: '12px', 
                            backgroundColor: user.status === 'active' ? '#d4edda' : '#f8d7da',
                            color: user.status === 'active' ? '#155724' : '#721c24',
                            fontSize: '12px'
                          }}>
                            {user.status || 'unknown'}
                          </span>
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '5px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => setEditingUser(user)}
                              style={{
                                padding: '4px 8px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '3px',
                                cursor: 'pointer',
                                fontSize: '11px'
                              }}
                            >
                              ✏️ Edit
                            </button>
                            <button
                              onClick={() => handleToggleStatus(user)}
                              style={{
                                padding: '4px 8px',
                                backgroundColor: user.status === 'active' ? '#ffc107' : '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '3px',
                                cursor: 'pointer',
                                fontSize: '11px'
                              }}
                            >
                              {user.status === 'active' ? '⏸️ Inactivate' : '▶️ Activate'}
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user)}
                              disabled={deletingUserId === user.id}
                              style={{
                                padding: '4px 8px',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '3px',
                                cursor: deletingUserId === user.id ? 'wait' : 'pointer',
                                fontSize: '11px',
                                opacity: deletingUserId === user.id ? 0.7 : 1
                              }}
                            >
                              {deletingUserId === user.id ? '⏳' : '🗑️ Delete'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSuccess={() => {
            setEditingUser(null);
            fetchTenantUsers();
          }}
        />
      )}
    </div>
  );
};

// NEW: User Search Component
const UserSearchSection = () => {
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [searchedUser, setSearchedUser] = useState(null);
  const [allMatchingUsers, setAllMatchingUsers] = useState([]); // Store all matches
  const [userTenants, setUserTenants] = useState([]);
  const [userAnalytics, setUserAnalytics] = useState({});
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');

  const handleUserSearch = async () => {
    if (!userSearchQuery.trim()) {
      setSearchError('Please enter an email or name to search');
      return;
    }

    setSearchLoading(true);
    setSearchError('');
    setSearchedUser(null);
    setAllMatchingUsers([]);
    setUserTenants([]);
    setUserAnalytics({});

    try {
      console.log('Searching for user:', userSearchQuery);
      
      // Search in BOTH users and faculty_staff collections
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const facultyStaffSnapshot = await getDocs(collection(db, 'faculty_staff'));
      
      console.log('Total users in users collection:', usersSnapshot.size);
      console.log('Total users in faculty_staff collection:', facultyStaffSnapshot.size);
      
      // Combine both collections
      const allDocs = [...usersSnapshot.docs, ...facultyStaffSnapshot.docs];
      console.log('Total documents to search:', allDocs.length);
      
      const searchLower = userSearchQuery.trim().toLowerCase();
      
      const matchingUsers = allDocs.filter(doc => {
        const userData = doc.data();
        
        // Build searchable fields (handle both collection formats)
        const email = (userData.email || '').toLowerCase();
        const firstName = (userData.firstName || '').toLowerCase();
        const lastName = (userData.lastName || '').toLowerCase();
        const displayName = (userData.displayName || userData.name || '').toLowerCase();
        const fullName = `${firstName} ${lastName}`.trim();
        
        // Check all possible matches
        return email.includes(searchLower) || 
               firstName.includes(searchLower) ||
               lastName.includes(searchLower) ||
               displayName.includes(searchLower) ||
               fullName.includes(searchLower);
      });

      console.log('Matching users found:', matchingUsers.length);

      if (matchingUsers.length === 0) {
        setSearchError(`No user found matching "${userSearchQuery}". Try searching by email, first name, or last name.`);
        setSearchLoading(false);
        return;
      }

      // Store all matching users
      const matchingUsersList = matchingUsers.map(doc => ({
        id: doc.id,
        collection: doc.ref.parent.id,
        ...doc.data()
      }));
      
      setAllMatchingUsers(matchingUsersList);

      // If multiple matches, DON'T auto-select - show the selection interface
      if (matchingUsers.length > 1) {
        setSearchError(`Found ${matchingUsers.length} users matching "${userSearchQuery}". Select a user below to view details.`);
        setSearchLoading(false);
      } else {
        // Only auto-select if there's exactly one match
        await selectUser(matchingUsersList[0]);
      }

    } catch (err) {
      console.error('Error searching for user:', err);
      setSearchError('Failed to search for user: ' + err.message);
      setSearchLoading(false);
    }
  };

  const selectUser = async (user) => {
    setSearchLoading(true);
    setSearchError('');
    
    try {
      const userId = user.id;
      const userData = user;

      console.log('Selected user:', { 
        id: userId, 
        email: userData.email, 
        collection: user.collection 
      });

      setSearchedUser(userData);

      // Get tenant info for this user
      const tenantId = userData.tenantId;
      
      if (!tenantId) {
        console.log('User has no tenantId');
        setSearchLoading(false);
        return;
      }

      // Get tenant info
      const tenantSnapshot = await getDocs(collection(db, 'tenants'));
      const tenantDoc = tenantSnapshot.docs.find(doc => doc.id === tenantId);
      const tenantInfo = tenantDoc?.data();

      console.log('Tenant info:', tenantInfo);

      // Get user's activity in this tenant
      let totalAbsences = 0;
      let totalSubRequests = 0;
      const allActivity = [];

      // Only query if we have valid user identifiers
      if (userId) {
        try {
          const absencesQuery1 = query(
            collection(db, 'absences'),
            where('tenantId', '==', tenantId),
            where('userId', '==', userId)
          );
          const absencesSnapshot1 = await getDocs(absencesQuery1);
          totalAbsences += absencesSnapshot1.size;
          
          absencesSnapshot1.docs.forEach(d => {
            const date = d.data().createdAt?.toDate();
            if (date) allActivity.push(date);
          });
        } catch (err) {
          console.log('Error querying absences with userId:', err.message);
        }

        try {
          const subRequestsQuery1 = query(
            collection(db, 'subRequests'),
            where('tenantId', '==', tenantId),
            where('requesterUid', '==', userId)
          );
          const subRequestsSnapshot1 = await getDocs(subRequestsQuery1);
          totalSubRequests += subRequestsSnapshot1.size;
          
          subRequestsSnapshot1.docs.forEach(d => {
            const date = d.data().createdAt?.toDate();
            if (date) allActivity.push(date);
          });
        } catch (err) {
          console.log('Error querying subRequests with requesterUid:', err.message);
        }
      }

      // Also try with uid field if it exists and is different from userId
      if (userData.uid && userData.uid !== userId) {
        try {
          const absencesQuery2 = query(
            collection(db, 'absences'),
            where('tenantId', '==', tenantId),
            where('userId', '==', userData.uid)
          );
          const absencesSnapshot2 = await getDocs(absencesQuery2);
          totalAbsences += absencesSnapshot2.size;
          
          absencesSnapshot2.docs.forEach(d => {
            const date = d.data().createdAt?.toDate();
            if (date) allActivity.push(date);
          });
        } catch (err) {
          console.log('Error querying absences with uid:', err.message);
        }

        try {
          const subRequestsQuery2 = query(
            collection(db, 'subRequests'),
            where('tenantId', '==', tenantId),
            where('requesterUid', '==', userData.uid)
          );
          const subRequestsSnapshot2 = await getDocs(subRequestsQuery2);
          totalSubRequests += subRequestsSnapshot2.size;
          
          subRequestsSnapshot2.docs.forEach(d => {
            const date = d.data().createdAt?.toDate();
            if (date) allActivity.push(date);
          });
        } catch (err) {
          console.log('Error querying subRequests with uid:', err.message);
        }
      }

      console.log('Activity counts:', {
        absences: totalAbsences,
        subRequests: totalSubRequests
      });

      // Get last activity date
      const lastActivity = allActivity.length > 0 
        ? new Date(Math.max(...allActivity.map(d => d.getTime())))
        : null;

      const tenantData = {
        tenantId,
        tenantName: tenantInfo?.name || tenantInfo?.schoolName || 'Unknown Tenant',
        role: userData.role || 'No Role',
        division: userData.division || userData.Division || 'N/A',
        createdAt: userData.createdAt?.toDate ? userData.createdAt.toDate() : null,
        lastActivity
      };

      const analytics = {
        [tenantId]: {
          totalAbsences,
          totalSubRequests,
          lastActivity
        }
      };

      setUserTenants([tenantData]);
      setUserAnalytics(analytics);

      console.log('Search complete:', { tenantData, analytics });

    } catch (err) {
      console.error('Error loading user details:', err);
      setSearchError('Failed to load user details: ' + err.message);
    } finally {
      setSearchLoading(false);
    }
  };

  const clearUserSearch = () => {
    setUserSearchQuery('');
    setSearchedUser(null);
    setUserTenants([]);
    setUserAnalytics({});
    setSearchError('');
  };

  return (
    <div style={{
      backgroundColor: '#f8f9fa',
      padding: '20px',
      borderRadius: '8px',
      marginBottom: '30px',
      border: '2px solid #007bff'
    }}>
      <h2 style={{ marginTop: 0, color: '#007bff' }}>🔍 User Search & Analytics</h2>
      <p style={{ color: '#666', marginBottom: '15px' }}>
        Search for any user to see their tenant registrations and usage analytics
      </p>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        <input
          type="text"
          value={userSearchQuery}
          onChange={(e) => setUserSearchQuery(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleUserSearch()}
          placeholder="Enter email or name..."
          style={{
            flex: 1,
            padding: '10px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        />
        <button
          onClick={handleUserSearch}
          disabled={searchLoading}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: searchLoading ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
        >
          {searchLoading ? 'Searching...' : 'Search'}
        </button>
        {searchedUser && (
          <button
            onClick={clearUserSearch}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Clear
          </button>
        )}
      </div>

      {searchError && (
        <div style={{
          padding: '10px',
          backgroundColor: '#fff3cd',
          color: '#856404',
          borderRadius: '4px',
          marginBottom: '15px'
        }}>
          {searchError}
        </div>
      )}

      {/* Show all matching users if multiple found */}
      {allMatchingUsers.length > 1 && !searchedUser && (
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #ddd',
          marginBottom: '20px'
        }}>
          <h3 style={{ marginTop: 0, color: '#333' }}>
            Select a User ({allMatchingUsers.length} matches)
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {allMatchingUsers.map((user) => (
              <button
                key={user.id}
                onClick={() => selectUser(user)}
                style={{
                  padding: '15px',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s',
                  fontSize: '14px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#e9ecef';
                  e.target.style.borderColor = '#007bff';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#f8f9fa';
                  e.target.style.borderColor = '#dee2e6';
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                  {user.displayName || user.name || `${user.firstName} ${user.lastName}`}
                </div>
                <div style={{ color: '#666', fontSize: '13px' }}>
                  {user.email} • {user.role || 'No Role'} • {user.collection === 'users' ? 'Users' : 'Faculty/Staff'}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {searchedUser && (
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #ddd'
        }}>
          {/* Back button if there are multiple matches */}
          {allMatchingUsers.length > 1 && (
            <button
              onClick={() => {
                setSearchedUser(null);
                setUserTenants([]);
                setUserAnalytics({});
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                marginBottom: '15px'
              }}
            >
              ← Back to Search Results ({allMatchingUsers.length} users)
            </button>
          )}
          
          <h3 style={{ marginTop: 0, color: '#333' }}>
            👤 {searchedUser.displayName || `${searchedUser.firstName} ${searchedUser.lastName}`}
          </h3>
          <div style={{ marginBottom: '20px' }}>
            <p style={{ margin: '5px 0' }}>
              <strong>Email:</strong> {searchedUser.email}
            </p>
            <p style={{ margin: '5px 0' }}>
              <strong>User ID:</strong> {searchedUser.id}
            </p>
            <p style={{ margin: '5px 0' }}>
              <strong>Registered Tenants:</strong> {userTenants.length}
            </p>
          </div>

          {userTenants.length > 0 ? (
            <div>
              <h4 style={{ color: '#007bff', marginBottom: '15px' }}>
                Tenant Memberships & Activity
              </h4>
              {userTenants.map((tenant) => (
                <div
                  key={tenant.tenantId}
                  style={{
                    backgroundColor: '#f8f9fa',
                    padding: '15px',
                    borderRadius: '6px',
                    marginBottom: '15px',
                    border: '1px solid #e0e0e0'
                  }}
                >
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '10px'
                  }}>
                    <div>
                      <h5 style={{ margin: '0 0 10px 0', color: '#333' }}>
                        🏢 {tenant.tenantName}
                      </h5>
                      <p style={{ margin: '5px 0', fontSize: '14px' }}>
                        <strong>Role:</strong> {tenant.role}
                      </p>
                      <p style={{ margin: '5px 0', fontSize: '14px' }}>
                        <strong>Division:</strong> {tenant.division}
                      </p>
                      <p style={{ margin: '5px 0', fontSize: '14px' }}>
                        <strong>Joined:</strong>{' '}
                        {tenant.createdAt
                          ? tenant.createdAt.toLocaleDateString()
                          : 'Unknown'}
                      </p>
                    </div>
                    <div>
                      <h5 style={{ margin: '0 0 10px 0', color: '#28a745' }}>
                        📊 Usage Statistics
                      </h5>
                      <p style={{ margin: '5px 0', fontSize: '14px' }}>
                        <strong>Total Absences:</strong>{' '}
                        {userAnalytics[tenant.tenantId]?.totalAbsences || 0}
                      </p>
                      <p style={{ margin: '5px 0', fontSize: '14px' }}>
                        <strong>Sub Requests:</strong>{' '}
                        {userAnalytics[tenant.tenantId]?.totalSubRequests || 0}
                      </p>
                      <p style={{ margin: '5px 0', fontSize: '14px' }}>
                        <strong>Last Activity:</strong>{' '}
                        {userAnalytics[tenant.tenantId]?.lastActivity
                          ? userAnalytics[tenant.tenantId].lastActivity.toLocaleDateString() +
                            ' ' +
                            userAnalytics[tenant.tenantId].lastActivity.toLocaleTimeString()
                          : 'No activity'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {/* Summary Statistics */}
              <div style={{
                backgroundColor: '#e7f3ff',
                padding: '15px',
                borderRadius: '6px',
                marginTop: '20px',
                border: '1px solid #007bff'
              }}>
                <h5 style={{ margin: '0 0 10px 0', color: '#007bff' }}>
                  📈 Overall Summary
                </h5>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
                  <div>
                    <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#007bff' }}>
                      {userTenants.length}
                    </p>
                    <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                      Total Tenants
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>
                      {Object.values(userAnalytics).reduce((sum, a) => sum + (a.totalAbsences || 0), 0)}
                    </p>
                    <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                      Total Absences
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#ffc107' }}>
                      {Object.values(userAnalytics).reduce((sum, a) => sum + (a.totalSubRequests || 0), 0)}
                    </p>
                    <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                      Total Sub Requests
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div style={{
              padding: '20px',
              textAlign: 'center',
              color: '#666',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px'
            }}>
              <p>This user is not registered in any tenants.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Tenant Management Component
const TenantManagement = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showTenantDetails, setShowTenantDetails] = useState(false);
  const [error, setError] = useState('');
  const [deletingTenantId, setDeletingTenantId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const functions = getFunctions(app, 'us-east1');
      const getAllTenants = httpsCallable(functions, 'getAllTenants');
      const result = await getAllTenants();
      
      if (result.data && result.data.tenants) {
        setTenants(result.data.tenants);
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);
      setError('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTenant = async (tenant) => {
    try {
      setDeletingTenantId(tenant.tenantId);
      const functions = getFunctions(app);
      const moveTenantToDeleted = httpsCallable(functions, 'moveTenantToDeleted');
      
      const result = await moveTenantToDeleted({
        tenantId: tenant.tenantId,
        tenantData: tenant
      });

      if (result.data.success) {
        setTenants(prevTenants => prevTenants.filter(t => t.tenantId !== tenant.tenantId));
        alert(`✅ ${tenant.name || tenant.schoolName} has been moved to deleted tenants.`);
      } else {
        throw new Error(result.data.error || 'Failed to delete tenant');
      }
    } catch (error) {
      console.error('Error deleting tenant:', error);
      alert(`❌ Error deleting tenant: ${error.message}`);
    } finally {
      setDeletingTenantId(null);
      setShowDeleteConfirm(null);
    }
  };

  const sortedTenants = [...tenants].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];
    
    if (sortBy === 'createdAt') {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    } else if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }
    
    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading tenants...</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>All Tenants ({tenants.length})</h3>
        <button
          onClick={fetchTenants}
          style={{
            padding: '8px 16px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          🔄 Refresh
        </button>
      </div>

      {error && (
        <div style={{ 
          padding: '10px', 
          backgroundColor: '#f8d7da', 
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
          color: '#721c24',
          marginBottom: '15px'
        }}>
          {error}
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th 
                style={{ padding: '10px', textAlign: 'left', cursor: 'pointer', borderBottom: '2px solid #dee2e6' }}
                onClick={() => handleSort('name')}
              >
                School Name {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                style={{ padding: '10px', textAlign: 'left', cursor: 'pointer', borderBottom: '2px solid #dee2e6' }}
                onClick={() => handleSort('subdomain')}
              >
                Subdomain {sortBy === 'subdomain' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                style={{ padding: '10px', textAlign: 'left', cursor: 'pointer', borderBottom: '2px solid #dee2e6' }}
                onClick={() => handleSort('tenantId')}
              >
                Tenant ID {sortBy === 'tenantId' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                style={{ padding: '10px', textAlign: 'left', cursor: 'pointer', borderBottom: '2px solid #dee2e6' }}
                onClick={() => handleSort('createdAt')}
              >
                Created {sortBy === 'createdAt' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>
                Status
              </th>
              <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedTenants.map((tenant) => (
              <tr key={tenant.tenantId} style={{ borderBottom: '1px solid #dee2e6' }}>
                <td style={{ padding: '10px' }}>{tenant.name || tenant.schoolName}</td>
                <td style={{ padding: '10px' }}>
                  <a 
                    href={`https://${tenant.subdomain}.reliefroster.com`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: '#007bff', textDecoration: 'none' }}
                  >
                    {tenant.subdomain}
                  </a>
                </td>
                <td style={{ padding: '10px', fontFamily: 'monospace', fontSize: '12px' }}>{tenant.tenantId}</td>
                <td style={{ padding: '10px' }}>
                  {new Date(tenant.createdAt).toLocaleDateString()}
                </td>
                <td style={{ padding: '10px' }}>
                  <span style={{ 
                    padding: '2px 8px', 
                    borderRadius: '12px', 
                    backgroundColor: tenant.active ? '#d4edda' : '#f8d7da',
                    color: tenant.active ? '#155724' : '#721c24',
                    fontSize: '12px'
                  }}>
                    {tenant.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={{ padding: '10px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: '5px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => {
                        setSelectedTenant(tenant);
                        setShowCreateUser(true);
                      }}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      ➕ Add User
                    </button>
                    <button
                      onClick={() => {
                        setSelectedTenant(tenant);
                        setShowTenantDetails(true);
                      }}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#17a2b8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      📊 View Details
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(tenant.tenantId)}
                      disabled={deletingTenantId === tenant.tenantId}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: deletingTenantId === tenant.tenantId ? '#dc3545' : '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: deletingTenantId === tenant.tenantId ? 'wait' : 'pointer',
                        fontSize: '12px',
                        opacity: deletingTenantId === tenant.tenantId ? 0.7 : 1
                      }}
                    >
                      {deletingTenantId === tenant.tenantId ? '⏳ Deleting...' : '🗑️ Delete'}
                    </button>
                  </div>
                  
                  {showDeleteConfirm === tenant.tenantId && (
                    <div style={{
                      position: 'absolute',
                      backgroundColor: 'white',
                      border: '1px solid #dc3545',
                      borderRadius: '4px',
                      padding: '15px',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                      zIndex: 100,
                      marginTop: '5px',
                      minWidth: '250px'
                    }}>
                      <p style={{ margin: '0 0 10px 0', fontSize: '14px' }}>
                        Are you sure you want to delete <strong>{tenant.name || tenant.schoolName}</strong>?
                      </p>
                      <p style={{ margin: '0 0 15px 0', fontSize: '12px', color: '#666' }}>
                        This will move the tenant to the deleted tenants collection.
                      </p>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                          onClick={() => handleDeleteTenant(tenant)}
                          style={{
                            flex: 1,
                            padding: '8px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Yes, Delete
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(null)}
                          style={{
                            flex: 1,
                            padding: '8px',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showTenantDetails && selectedTenant && (
        <TenantDetailsModal 
          tenant={selectedTenant}
          onClose={() => {
            setShowTenantDetails(false);
            setSelectedTenant(null);
          }}
        />
      )}

      {showCreateUser && selectedTenant && (
        <CreateUserModal 
          tenant={selectedTenant}
          onClose={() => {
            setShowCreateUser(false);
            setSelectedTenant(null);
          }}
          onSuccess={() => {
            setShowCreateUser(false);
            setSelectedTenant(null);
          }}
        />
      )}
    </div>
  );
};

// Create User Modal Component
const CreateUserModal = ({ tenant, onClose, onSuccess }) => {
  const [userData, setUserData] = useState({
    department: '',
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    role: 'Faculty/Staff',
    sis_id: '',
    status: 'active',
    supervisorId: '',
    tenantId: tenant.tenantId,
    title: '',
    uid: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleInputChange = (field, value) => {
    setUserData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!userData.email || !userData.firstName || !userData.lastName) {
      setError('Email, First Name, and Last Name are required.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const functions = getFunctions(app, 'us-east1');
      const createUserForTenant = httpsCallable(functions, 'createUserForTenant');
      
      const result = await createUserForTenant({
        ...userData,
        tenantId: tenant.tenantId
      });

      if (result.data.success) {
        setSuccess(`User created successfully! UID: ${result.data.uid}`);
        setTimeout(() => {
          onSuccess();
        }, 2000);
      } else {
        throw new Error(result.data.error || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      setError(error.message || 'An error occurred while creating the user');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '8px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <h2 style={{ marginTop: 0 }}>Create User for {tenant.name || tenant.schoolName}</h2>
        <p style={{ color: '#666', marginBottom: '20px' }}>Tenant ID: {tenant.tenantId}</p>

        {error && (
          <div style={{ 
            padding: '10px', 
            backgroundColor: '#f8d7da', 
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            color: '#721c24',
            marginBottom: '15px'
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{ 
            padding: '10px', 
            backgroundColor: '#d4edda', 
            border: '1px solid #c3e6cb',
            borderRadius: '4px',
            color: '#155724',
            marginBottom: '15px'
          }}>
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <FormInput
              label="First Name*"
              id="firstName"
              type="text"
              value={userData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              required
            />
            
            <FormInput
              label="Last Name*"
              id="lastName"
              type="text"
              value={userData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              required
            />
          </div>

          <FormInput
            label="Email*"
            id="email"
            type="email"
            value={userData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            required
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <FormInput
              label="Phone"
              id="phone"
              type="tel"
              value={userData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
            />

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                Role*
              </label>
              <select
                value={userData.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                }}
              >
                <option value="Faculty/Staff">Faculty/Staff</option>
                <option value="Staff">Staff</option>
                <option value="HR">HR</option>
                <option value="System Administrator">System Administrator</option>
                <option value="Educational Administrator">Educational Administrator</option>
                <option value="Outside Sub">Outside Sub</option>
                <option value="Volunteer Manager">Volunteer Manager</option>
                <option value="Volunteer">Volunteer</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <FormInput
              label="Department"
              id="department"
              type="text"
              value={userData.department}
              onChange={(e) => handleInputChange('department', e.target.value)}
            />

            <FormInput
              label="Title"
              id="title"
              type="text"
              value={userData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <FormInput
              label="SIS ID"
              id="sis_id"
              type="text"
              value={userData.sis_id}
              onChange={(e) => handleInputChange('sis_id', e.target.value)}
            />

            <FormInput
              label="Supervisor ID"
              id="supervisorId"
              type="text"
              value={userData.supervisorId}
              onChange={(e) => handleInputChange('supervisorId', e.target.value)}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
              Status
            </label>
            <select
              value={userData.status}
              onChange={(e) => handleInputChange('status', e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxSizing: 'border-box'
              }}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: isSubmitting ? '#6c757d' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontWeight: 'bold'
              }}
            >
              {isSubmitting ? 'Creating User...' : 'Create User'}
            </button>
            
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const SuperAdminPage = () => {
  const [schoolName, setSchoolName] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [adminFirstName, setAdminFirstName] = useState('');
  const [adminLastName, setAdminLastName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [activeSection, setActiveSection] = useState('requests');
  const [showManualWizard, setShowManualWizard] = useState(false);

  const handleSchoolNameChange = (e) => {
    const name = e.target.value;
    setSchoolName(name);
    const slug = name.toLowerCase().replace(/[^a-z0-9]/g, '');
    setSubdomain(slug);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!schoolName || !subdomain || !adminFirstName || !adminLastName || !adminEmail) {
      setErrorMessage('All fields are required.');
      return;
    }

    if (!app) {
      setErrorMessage("Firebase has not been initialized correctly. Please check the console.");
      console.error("Firebase app object is missing. Check your firebase/config.js export.");
      return;
    }
    
    setIsSubmitting(true);
    setSuccessMessage('');
    setErrorMessage('');

    const newTenantData = {
      schoolName,
      subdomain,
      adminFirstName,
      adminLastName,
      adminEmail,
    };
    
    const functions = getFunctions(app, 'us-east1');
    const createNewTenant = httpsCallable(functions, 'createNewTenant_v2');

    try {
      const result = await createNewTenant(newTenantData);
      
      console.log('Cloud function success:', result.data);
      setSuccessMessage(result.data.message || 'Tenant created successfully!');
      
      setSchoolName('');
      setSubdomain('');
      setAdminFirstName('');
      setAdminLastName('');
      setAdminEmail('');

    } catch (error) {
      console.error("Error calling createNewTenant function:", error);
      setErrorMessage(error.message || 'An unexpected error occurred.');
    }

    setIsSubmitting(false);
  };

  const handleManualSubmit = async (formData) => {
    try {
      setIsSubmitting(true);
      
      console.log('Creating tenant directly:', formData);
      
      const response = await fetch('https://us-east1-relief-roster-43593.cloudfunctions.net/createTenantDirectly', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      console.log('Response status:', response.status);
      const result = await response.json();
      console.log('Response result:', result);

      if (result.success) {
        setSuccessMessage(`🎉 Tenant created successfully!\n\n` +
          `School: ${result.schoolName}\n` +
          `URL: ${result.url}\n` +
          `Tenant ID: ${result.tenantId}\n\n` +
          `Our team will finalize your onboarding and be in touch shortly!`);
        
        setShowManualWizard(false);
        
        setTimeout(() => {
          setSuccessMessage('');
        }, 10000);
        
      } else {
        throw new Error(result.error || 'Failed to create tenant');
      }

    } catch (error) {
      console.error('Error creating tenant:', error);
      setErrorMessage(`❌ Error: ${error.message}`);
      
      setTimeout(() => {
        setErrorMessage('');
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '20px auto', fontFamily: 'sans-serif' }}>
      <h1 style={{ borderBottom: '2px solid #eee', paddingBottom: '10px' }}>Super Admin Control Panel</h1>
      
      {successMessage && (
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#d4edda', 
          border: '1px solid #c3e6cb',
          borderRadius: '4px',
          color: '#155724',
          marginBottom: '20px',
          fontSize: '14px',
          whiteSpace: 'pre-line'
        }}>
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#f8d7da', 
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
          color: '#721c24',
          marginBottom: '20px',
          fontSize: '14px'
        }}>
          {errorMessage}
        </div>
      )}
      
      <div style={{ 
        display: 'flex', 
        gap: '10px', 
        marginBottom: '30px',
        borderBottom: '1px solid #ddd'
      }}>
        <button
          onClick={() => setActiveSection('requests')}
          style={{
            padding: '10px 20px',
            border: 'none',
            backgroundColor: activeSection === 'requests' ? '#007bff' : 'transparent',
            color: activeSection === 'requests' ? 'white' : '#007bff',
            borderBottom: activeSection === 'requests' ? 'none' : '2px solid transparent',
            cursor: 'pointer',
            borderRadius: '5px 5px 0 0'
          }}
        >
          📋 Pending Requests
        </button>
        <button
          onClick={() => setActiveSection('wizard')}
          style={{
            padding: '10px 20px',
            border: 'none',
            backgroundColor: activeSection === 'wizard' ? '#28a745' : 'transparent',
            color: activeSection === 'wizard' ? 'white' : '#28a745',
            borderBottom: activeSection === 'wizard' ? 'none' : '2px solid transparent',
            cursor: 'pointer',
            borderRadius: '5px 5px 0 0'
          }}
        >
          🧙‍♂️ School Setup
        </button>
        <button
          onClick={() => setActiveSection('tenants')}
          style={{
            padding: '10px 20px',
            border: 'none',
            backgroundColor: activeSection === 'tenants' ? '#6f42c1' : 'transparent',
            color: activeSection === 'tenants' ? 'white' : '#6f42c1',
            borderBottom: activeSection === 'tenants' ? 'none' : '2px solid transparent',
            cursor: 'pointer',
            borderRadius: '5px 5px 0 0'
          }}
        >
          🏫 Manage Tenants
        </button>
        <button
          onClick={() => setActiveSection('userSearch')}
          style={{
            padding: '10px 20px',
            border: 'none',
            backgroundColor: activeSection === 'userSearch' ? '#17a2b8' : 'transparent',
            color: activeSection === 'userSearch' ? 'white' : '#17a2b8',
            borderBottom: activeSection === 'userSearch' ? 'none' : '2px solid transparent',
            cursor: 'pointer',
            borderRadius: '5px 5px 0 0'
          }}
        >
          🔍 User Search
        </button>
      </div>

      {activeSection === 'requests' && (
        <div>
          <TenantRequestsManager />
        </div>
      )}

      {activeSection === 'wizard' && (
        <div style={{ 
          padding: '20px', 
          border: '1px solid #ddd', 
          borderRadius: '8px', 
          backgroundColor: '#f0f8ff', 
          borderLeft: '4px solid #007bff'
        }}>
          <h2 style={{ marginTop: 0, marginBottom: '15px', color: '#007bff' }}>School Setup & Onboarding</h2>
          <p style={{ marginBottom: '20px', color: '#555' }}>
            Choose how to onboard a new school - send them an invitation to fill out their own information, or fill it out manually for them.
          </p>
          
          <SimpleInvitationSection onInviteSent={(result) => {
            console.log('Invitation sent:', result);
          }} />

          <div style={{ 
            padding: '20px', 
            border: '1px solid #6f42c1', 
            borderRadius: '8px', 
            backgroundColor: '#faf9ff' 
          }}>
            <h3 style={{ color: '#6f42c1', marginTop: 0 }}>📝 Manual Entry (For Schools Needing Help)</h3>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
              Fill out all the detailed school information yourself using our comprehensive 6-step wizard.
            </p>
            <button
              onClick={() => setShowManualWizard(true)}
              disabled={isSubmitting}
              style={{
                padding: '12px 24px',
                backgroundColor: isSubmitting ? '#6c757d' : '#6f42c1',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontWeight: 'bold',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              {isSubmitting ? '⏳ Processing...' : '📝 Open Manual Entry Wizard'}
            </button>
          </div>

          <div style={{ fontSize: '14px', color: '#666', marginTop: '20px' }}>
            <p><strong>Advanced Wizard Features:</strong></p>
            <ul style={{ marginLeft: '20px' }}>
              <li>Comprehensive school information collection</li>
              <li>Branding and customization options</li>
              <li>Integration preferences</li>
              <li>Detailed configuration settings</li>
              <li>Request approval workflow</li>
            </ul>
          </div>
        </div>
      )}

      {activeSection === 'tenants' && (
        <div style={{ 
          padding: '20px', 
          border: '1px solid #ddd', 
          borderRadius: '8px', 
          backgroundColor: '#f9f9f9' 
        }}>
          <h2 style={{ marginTop: 0, marginBottom: '20px' }}>🏫 Tenant Management</h2>
          <TenantManagement />
        </div>
      )}

      {activeSection === 'userSearch' && (
        <div>
          <UserSearchSection />
        </div>
      )}

      {showManualWizard && (
        <ManualTenantWizard 
          onClose={() => setShowManualWizard(false)}
          onSubmit={handleManualSubmit}
        />
      )}
    </div>
  );
};

export default SuperAdminPage;